import { useQuery } from "@tanstack/react-query";
import { Download, FileArchive, FileDown, Save } from "lucide-react";
import { type FormEvent, useState } from "react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { api, download, jsonBody, queryString } from "../../api/client";
import type { AnalyticsDashboard, ExportJob, ListResponse } from "../../api/types";
import { useVenueFilter, VenueFilterSelect } from "../../app/venueFilter";
import { useWorkspaceLens } from "../../app/workspaceLens";
import { formatDate, formatMoney, formatPercent, titleCase } from "../../lib/format";
import {
  Card,
  CardHeader,
  DataTable,
  EmptyState,
  ErrorPanel,
  Field,
  FormActions,
  Metric,
  Modal,
  Notice,
  PageHeader,
  StatusPill,
} from "../../ui/components";
import { AnalyticsSkeleton } from "../../ui/skeletons";

interface SavedReport {
  id: string;
  name: string;
  reportKey: string;
  visibility: string;
  version: number;
  createdAt: string;
  updatedAt: string;
}
const reportKeys = [
  "dashboard",
  "bookings",
  "events",
  "invoices",
  "payments",
  "sponsors",
  "inventory",
  "audit",
  "integration-deliveries",
];

function defaultFrom() {
  const date = new Date();
  date.setMonth(date.getMonth() - 3);
  return date.toISOString().slice(0, 10);
}
function defaultTo() {
  return new Date().toISOString().slice(0, 10);
}

export function AnalyticsPage() {
  const [from, setFrom] = useState(defaultFrom);
  const [to, setTo] = useState(defaultTo);
  const lens = useWorkspaceLens();
  // The top bar decides the venue, and this is one of the two pages allowed to widen back out
  // from there to every venue this person looks after.
  const filter = useVenueFilter();
  const [exportOpen, setExportOpen] = useState(false);
  const [exportType, setExportType] = useState("csv");
  const [saveOpen, setSaveOpen] = useState(false);
  const [error, setError] = useState<unknown>();
  const [working, setWorking] = useState(false);
  const filterQuery = queryString({ from, to, venueId: filter.venueId });
  const dashboard = useQuery({
    queryKey: ["analytics", "dashboard", filterQuery],
    queryFn: () => api<AnalyticsDashboard>(`/api/v1/analytics/dashboard${filterQuery}`),
    enabled: filter.ready,
  });
  const exportsQuery = useQuery({
    queryKey: ["analytics", "exports"],
    queryFn: () => api<ListResponse<ExportJob>>("/api/v1/analytics/exports?limit=50"),
    refetchInterval: (query) =>
      query.state.data?.items.some((item) => ["queued", "running"].includes(item.status))
        ? 2000
        : false,
  });
  const saved = useQuery({
    queryKey: ["analytics", "saved-reports"],
    queryFn: () => api<ListResponse<SavedReport>>("/api/v1/analytics/saved-reports"),
  });
  // The dashboard serves both halves of the product. Somebody who holds no role on a side
  // would only see that side's zeros, so its figures are left out for them entirely, and a
  // member who holds both reads the two groups under their own names, the open workspace's
  // group first.
  const lettings = lens.canEnter("bookings");
  const programme = lens.canEnter("events");

  if (!filter.ready || dashboard.isLoading) return <AnalyticsSkeleton />;
  if (dashboard.error) return <ErrorPanel error={dashboard.error} />;
  const data = dashboard.data;
  if (!data) return null;
  const filters = {
    from: new Date(`${from}T00:00:00Z`).toISOString(),
    to: new Date(`${to}T23:59:59Z`).toISOString(),
    venueIds: filter.venueId ? [filter.venueId] : [],
    locationIds: [],
    statuses: [],
    sources: [],
    customerTypes: [],
  };

  async function requestExport(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    const type = String(form.get("type"));
    const reportKey =
      type === "bulk_zip" ? "bulk" : type === "pdf" ? "dashboard" : String(form.get("reportKey"));
    setWorking(true);
    setError(undefined);
    try {
      await api("/api/v1/analytics/exports", {
        method: "POST",
        body: jsonBody({ type, reportKey, filters }),
      });
      await exportsQuery.refetch();
      setExportOpen(false);
    } catch (caught) {
      setError(caught);
    } finally {
      setWorking(false);
    }
  }

  async function saveReport(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    setWorking(true);
    setError(undefined);
    try {
      await api("/api/v1/analytics/saved-reports", {
        method: "POST",
        body: jsonBody({
          name: String(form.get("name")),
          reportKey: String(form.get("reportKey")),
          filters,
          visibility: String(form.get("visibility")),
          version: 0,
        }),
      });
      await saved.refetch();
      setSaveOpen(false);
    } catch (caught) {
      setError(caught);
    } finally {
      setWorking(false);
    }
  }

  const chartTimeline = data.timeline.map((item) => ({
    ...item,
    cashReceived: item.cashReceivedMinor / 100,
  }));

  const programmeCards = programme ? (
    <>
      <Card>
        <CardHeader
          title="Your events"
          description="Events your own team runs: how many are on, who is expected, and what they are budgeted at."
        />
        <div className="metrics-grid">
          <Metric
            label="Events"
            value={data.events.totalEvents}
            hint={`${data.events.confirmedEvents} confirmed`}
          />
          <Metric label="Expected attendance" value={data.events.expectedAttendance} />
          <Metric label="Budgeted" value={formatMoney(data.events.budgetMinor, data.currency)} />
          <Metric
            label="Room-hours held"
            value={`${data.events.roomHours.toFixed(1)}h`}
            hint={`${data.events.hiredEvents} tied to a booking`}
          />
        </div>
      </Card>
      <Card>
        <CardHeader
          title="Sponsorship"
          description="Sponsor money raised for your own events. In-kind gifts carry a fair value for the record and are never counted as money to spend."
        />
        <div className="metrics-grid">
          <Metric
            label="Sponsor cash received"
            value={formatMoney(data.summary.sponsorCashReceivedMinor, data.currency)}
          />
          <Metric
            label="In-kind fair value"
            value={formatMoney(data.summary.sponsorInKindFairValueMinor, data.currency)}
            hint="Never included in cash"
          />
        </div>
      </Card>
    </>
  ) : null;
  const rentalCard = lettings ? (
    <Card>
      <CardHeader
        title="Rental income"
        description="Bookings taken, how well the space is used, and what renters have been billed and have paid."
      />
      <div className="metrics-grid">
        <Metric
          label="Total bookings"
          value={data.summary.totalBookings}
          hint={`${data.summary.confirmedBookings} confirmed`}
        />
        <Metric label="Conversion" value={formatPercent(data.summary.conversionBasisPoints)} />
        <Metric label="Utilization" value={formatPercent(data.summary.utilisationBasisPoints)} />
        <Metric label="Average lead time" value={`${data.summary.averageLeadHours.toFixed(1)}h`} />
        <Metric
          label="Invoice total"
          value={formatMoney(data.summary.invoiceTotalMinor, data.currency)}
        />
        <Metric
          label="Cash received"
          value={formatMoney(data.summary.cashReceivedMinor, data.currency)}
        />
      </div>
    </Card>
  ) : null;

  return (
    <>
      <PageHeader
        title="Analytics and exports"
        description={
          lens.isEvents
            ? "Results for the events you run yourselves, their sponsorship, and the space they take, with rental figures alongside where you also let space."
            : "Booking, income, sponsor, and inventory results for the selected venue, or for every venue you look after."
        }
        actions={
          <div className="inline">
            <button className="button secondary" type="button" onClick={() => setSaveOpen(true)}>
              <Save size={17} /> Save view
            </button>
            <button className="button primary" type="button" onClick={() => setExportOpen(true)}>
              <FileDown size={17} /> Export
            </button>
          </div>
        }
      />
      <Card>
        <div className="filters">
          <Field label="From">
            <input type="date" value={from} onChange={(event) => setFrom(event.target.value)} />
          </Field>
          <Field label="To">
            <input type="date" value={to} onChange={(event) => setTo(event.target.value)} />
          </Field>
          <VenueFilterSelect filter={filter} />
        </div>
      </Card>
      {/* The open workspace's own figures come first; the other half's follow for somebody who
          holds both roles, and stay out entirely for somebody who does not. */}
      {lens.isEvents ? programmeCards : rentalCard}
      {lens.isEvents ? rentalCard : programmeCards}
      <div className="content-grid">
        {/* Bookings over time, by venue and by space all read the rental side, so they are left
            out for somebody who holds no role there rather than drawn empty. */}
        {lettings ? (
          <>
            <Card className="span-7">
              <CardHeader
                title="Activity over time"
                description="Bookings compared with cash received."
              />
              <div className="chart">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={chartTimeline}>
                    <CartesianGrid stroke="var(--border)" vertical={false} />
                    <XAxis dataKey="period" tick={{ fill: "var(--text-muted)", fontSize: 12 }} />
                    <YAxis yAxisId="left" tick={{ fill: "var(--text-muted)", fontSize: 12 }} />
                    <YAxis
                      yAxisId="right"
                      orientation="right"
                      tick={{ fill: "var(--text-muted)", fontSize: 12 }}
                    />
                    <Tooltip
                      contentStyle={{
                        background: "var(--surface-raised)",
                        border: "1px solid var(--border)",
                        borderRadius: "0.75rem",
                      }}
                    />
                    <Legend />
                    <Line
                      yAxisId="left"
                      type="monotone"
                      dataKey="bookingCount"
                      name="Bookings"
                      stroke="var(--chart-1)"
                      strokeWidth={2}
                    />
                    <Line
                      yAxisId="right"
                      type="monotone"
                      dataKey="cashReceived"
                      name={`Cash received (${data.currency})`}
                      stroke="var(--chart-2)"
                      strokeWidth={2}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </Card>
            <Card className="span-5">
              <CardHeader title="Venue breakdown" />
              <div className="chart">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={data.venues} layout="vertical">
                    <CartesianGrid stroke="var(--border)" horizontal={false} />
                    <XAxis type="number" tick={{ fill: "var(--text-muted)", fontSize: 12 }} />
                    <YAxis
                      type="category"
                      dataKey="venueName"
                      width={105}
                      tick={{ fill: "var(--text-muted)", fontSize: 12 }}
                    />
                    <Tooltip
                      contentStyle={{
                        background: "var(--surface-raised)",
                        border: "1px solid var(--border)",
                        borderRadius: "0.75rem",
                      }}
                    />
                    <Bar
                      dataKey="bookingCount"
                      name="Bookings"
                      fill="var(--chart-1)"
                      radius={[0, 5, 5, 0]}
                    />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </Card>
            <Card className="span-12">
              <CardHeader
                title="Space-by-space results"
                description="See bookings and booked hours for every space in your current view."
              />
              {data.locations.length ? (
                <DataTable
                  caption="Location analytics"
                  items={data.locations}
                  getKey={(item) => item.locationId}
                  columns={[
                    {
                      label: "Location",
                      render: (item) => (
                        <>
                          <strong>{item.locationName}</strong>
                          <br />
                          <small>{item.venueName}</small>
                        </>
                      ),
                    },
                    { label: "Capacity", render: (item) => item.capacity ?? "Not available" },
                    {
                      label: "Bookings",
                      className: "numeric",
                      render: (item) => item.bookingCount,
                    },
                    {
                      label: "Booked hours",
                      className: "numeric",
                      render: (item) => item.bookedHours.toFixed(1),
                    },
                  ]}
                />
              ) : (
                <EmptyState
                  title="No location data"
                  description="Broaden the date range or add bookings to generate a breakdown."
                />
              )}
            </Card>
          </>
        ) : null}
        <Card className="span-5">
          <CardHeader title="Saved reports" />
          {saved.data?.items.length ? (
            <div className="stack">
              {saved.data.items.map((item) => (
                <div className="split" key={item.id}>
                  <span>
                    <strong>{item.name}</strong>
                    <small>
                      {titleCase(item.reportKey)} · {titleCase(item.visibility)}
                    </small>
                  </span>
                  <StatusPill value="saved" />
                </div>
              ))}
            </div>
          ) : (
            <EmptyState
              title="No saved views"
              description="Save the current filters for repeat reporting."
            />
          )}
        </Card>
        <Card className="span-7">
          <CardHeader
            title="Export history"
            description="Your downloads appear here when they are ready."
          />
          {exportsQuery.data?.items.length ? (
            <DataTable
              caption="Analytics exports"
              items={exportsQuery.data.items}
              getKey={(item) => item.id}
              columns={[
                {
                  label: "Export",
                  render: (item) => (
                    <>
                      <strong>{item.filename || titleCase(item.reportKey)}</strong>
                      <br />
                      <small>
                        {titleCase(item.type)} · {formatDate(item.requestedAt)}
                      </small>
                    </>
                  ),
                },
                {
                  label: "Personal data",
                  render: (item) => (item.containsPersonalData ? "Contains personal data" : "No"),
                },
                { label: "Status", render: (item) => <StatusPill value={item.status} /> },
                {
                  label: "Download",
                  render: (item) =>
                    item.status === "ready" ? (
                      <button
                        className="button secondary small"
                        type="button"
                        onClick={() => download(`/api/v1/analytics/exports/${item.id}/download`)}
                      >
                        <Download size={15} /> Download
                      </button>
                    ) : (
                      item.error || "Not available"
                    ),
                },
              ]}
            />
          ) : (
            <EmptyState
              title="No exports"
              description="Request CSV, PDF, or bulk ZIP output for the current scope."
            />
          )}
        </Card>
      </div>
      {data.notes.length ? (
        <Notice tone="info" title="About these figures">
          {data.notes.join(" ")}
        </Notice>
      ) : null}
      <Modal
        open={exportOpen}
        onClose={() => setExportOpen(false)}
        title="Request export"
        description="We’ll prepare the file and keep it available for a limited time."
      >
        {error ? <ErrorPanel error={error} /> : null}
        <form className="form-stack" onSubmit={requestExport}>
          <Field label="Format">
            <select
              name="type"
              value={exportType}
              onChange={(event) => setExportType(event.target.value)}
            >
              <option value="csv">CSV</option>
              <option value="pdf">Dashboard PDF</option>
              <option value="bulk_zip">Bulk ZIP</option>
            </select>
          </Field>
          {/* Only CSV takes a report choice. A PDF is always the dashboard summary and a ZIP is
              always every report, so a list shown for those formats would be a choice the
              request quietly discards. */}
          {exportType === "csv" ? (
            <Field label="Report">
              <select name="reportKey" defaultValue={lens.isEvents ? "events" : "bookings"}>
                {reportKeys.map((key) => (
                  <option key={key} value={key}>
                    {titleCase(key)}
                  </option>
                ))}
              </select>
            </Field>
          ) : (
            <Notice tone="info">
              {exportType === "pdf"
                ? "The PDF is always the dashboard summary for the filters above."
                : "The ZIP contains one CSV for every report, using the filters above."}
            </Notice>
          )}
          <Notice tone="warning">
            <FileArchive size={17} /> Some reports include customer details. Your role must allow
            access to that information.
          </Notice>
          <FormActions
            submitting={working}
            submitLabel="Prepare export"
            onCancel={() => setExportOpen(false)}
          />
        </form>
      </Modal>
      <Modal open={saveOpen} onClose={() => setSaveOpen(false)} title="Save report view">
        {error ? <ErrorPanel error={error} /> : null}
        <form className="form-stack" onSubmit={saveReport}>
          <Field label="Name">
            <input name="name" required />
          </Field>
          <Field label="Report">
            <select name="reportKey" defaultValue="dashboard">
              {reportKeys.map((key) => (
                <option key={key} value={key}>
                  {titleCase(key)}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Visibility">
            <select name="visibility" defaultValue="private">
              <option value="private">Only me</option>
              <option value="tenant">Everyone with analytics access</option>
            </select>
          </Field>
          <FormActions
            submitting={working}
            submitLabel="Save view"
            onCancel={() => setSaveOpen(false)}
          />
        </form>
      </Modal>
    </>
  );
}
